import {
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartOptions, Plugin } from "chart.js";
import { Line } from "react-chartjs-2";
import type {
  EnrollmentCohortStatusOptions,
  EnrollmentCohortStatusResponse,
} from "@t3tools/contracts";
import { Button } from "~/components/ui/button";
import {
  ChartPanel,
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
} from "./dashboardKit";
import { EnrollmentClinicMultiSelect } from "./enrollmentClinicMultiSelect";
import { cohortStatusDatasets, cohortStatusStyles } from "./enrollmentCohortStatus";

ChartJS.register(Filler, LinearScale, LineElement, PointElement, Tooltip, Legend);

function monthLabel(month: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${month}T00:00:00Z`));
}

export function EnrollmentCohortStatusSection(props: {
  options: EnrollmentCohortStatusOptions | undefined;
  data: EnrollmentCohortStatusResponse | undefined;
  clinicIds: string[];
  month: string;
  months: string[];
  loading: boolean;
  error: string;
  onClinicsChange: (ids: string[]) => void;
  onMonthChange: (month: string) => void;
  onRetry: () => void;
}) {
  const data = props.data;
  const { keys, datasets } = data ? cohortStatusDatasets(data) : { keys: [], datasets: [] };
  const newKeys = keys.filter((key) => cohortStatusStyles[key].group === "New");
  const otherKeys = keys.filter((key) => cohortStatusStyles[key].group === "GUIDE");
  const followupMarker: Plugin<"line"> = {
    id: "cohort-full-followup",
    afterDatasetsDraw: (chart) => {
      if (data?.fullyObservedDays === null || data?.fullyObservedDays === undefined) return;
      const x = chart.scales.x?.getPixelForValue(data.fullyObservedDays + 1);
      if (x === undefined || x >= chart.chartArea.right) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x, chart.chartArea.top);
      ctx.lineTo(x, chart.chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: data?.points.length,
        title: { display: true, text: "Days since Carespace creation (day 0 = creation day)" },
        ticks: { precision: 0 },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        title: { display: true, text: "Share of observed Carespaces" },
        ticks: { callback: (value) => `${value}%` },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        filter: (item) => item.parsed.y !== 0,
        callbacks: {
          title: (items) => {
            const point =
              data?.points[Math.min(items[0]?.dataIndex ?? 0, (data?.points.length ?? 1) - 1)];
            return point
              ? `Day ${point.day} · ${point.observed} observed of ${data?.cohortSize}`
              : "";
          },
          label: (item) => {
            const point = data?.points[Math.min(item.dataIndex, (data?.points.length ?? 1) - 1)];
            const count =
              point?.counts.find((entry) => entry.status === keys[item.datasetIndex])?.count ?? 0;
            return `${item.dataset.label}: ${count} (${(item.parsed.y ?? 0).toFixed(1)}%)`;
          },
          footer: (items) =>
            (items[0]?.parsed.x ?? 0) > (data?.fullyObservedDays ?? Infinity)
              ? "Only Carespaces that reached this follow-up day are counted."
              : "",
        },
      },
    },
  };
  return (
    <section className="mb-6 flex flex-col gap-4" aria-label="Monthly cohort status">
      <h2 className="text-lg font-semibold">Monthly cohort status</h2>
      <DashboardFilterRow
        trailing={data ? `Data through ${data.throughDate} · America/New_York` : undefined}
      >
        <DashboardFilterField label="Clinics">
          <EnrollmentClinicMultiSelect
            clinics={props.options?.clinics ?? []}
            selected={props.clinicIds}
            onChange={props.onClinicsChange}
            label="Cohort status clinics"
          />
        </DashboardFilterField>
        <DashboardFilterField label="Month / cohort">
          <DashboardSelect
            aria-label="Month / cohort"
            value={props.month}
            disabled={!props.months.length}
            onChange={(event) => props.onMonthChange(event.target.value)}
          >
            {props.months.map((month) => (
              <option key={month} value={month}>
                {monthLabel(month)}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
      </DashboardFilterRow>
      {props.error ? (
        <div role="alert" className="space-y-2 rounded-lg border border-border p-4">
          <p className="text-sm">{props.error}</p>
          <Button variant="outline" onClick={props.onRetry}>
            Try again
          </Button>
        </div>
      ) : props.loading ? (
        <p role="status" className="py-12 text-sm text-muted-foreground">
          Loading cohort statuses…
        </p>
      ) : !props.clinicIds.length ? (
        <p role="status" className="text-sm text-muted-foreground">
          Select clinics to display a cohort.
        </p>
      ) : !data?.points.length ? (
        <p role="status" className="text-sm text-muted-foreground">
          No Carespaces created in this month for the selected clinics.
        </p>
      ) : (
        <ChartPanel
          title={`${monthLabel(data.month)} · ${data.cohortSize.toLocaleString("en-US")} Carespaces`}
        >
          <p className="mb-3 text-xs text-muted-foreground">
            Selected clinics are combined. Every daily stack totals 100%. Full cohort observed
            through day {data.fullyObservedDays}; later days have fewer observed Carespaces. The
            dashed line marks this boundary.
          </p>
          <div className="h-[440px] min-w-0">
            <Line
              role="img"
              aria-label="Daily cohort status, New split by enrollment status"
              data={{ datasets }}
              options={chartOptions}
              plugins={[followupMarker]}
            />
          </div>
          <div className="mt-5 space-y-4">
            {[
              { label: "GUIDE = New · enrollment status on that day", keys: newKeys },
              { label: "Other GUIDE statuses", keys: otherKeys },
            ].map((group) =>
              group.keys.length ? (
                <fieldset key={group.label}>
                  <legend className="mb-2 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </legend>
                  <div className="grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4">
                    {group.keys.map((key) => (
                      <div key={key} className="flex items-center gap-2">
                        <span
                          aria-hidden
                          className="h-3 w-4 shrink-0 rounded-sm"
                          style={{ backgroundColor: cohortStatusStyles[key].color }}
                        />
                        <span>{cohortStatusStyles[key].label}</span>
                      </div>
                    ))}
                  </div>
                </fieldset>
              ) : null,
            )}
          </div>
        </ChartPanel>
      )}
      <details className="rounded-lg border border-border/70 p-4 text-sm">
        <summary className="cursor-pointer font-medium">
          How daily cohort statuses are calculated
        </summary>
        <div className="mt-3 space-y-2 text-muted-foreground">
          <p>
            Monthly cohorts include retained Carespaces created in that month, including archived
            Carespaces. Dates use America/New_York and end at the last completed day. The current
            month's cohort can still grow.
          </p>
          <p>
            Each day uses the latest GUIDE effective status. New is split by the latest enrollment
            status recorded by that day. Missing GUIDE history counts as New; missing enrollment
            history is shown separately.
          </p>
          <p>
            Not eligible combines archived periods (starting at the archive record's creation date
            and ending on unarchive) with the latest enrollment status Ineligible for GUIDE.
          </p>
          <p>
            Only Carespaces that have reached a follow-up day enter that day's denominator. Hover to
            see counts. Colors stay consistent across clinics and months.
          </p>
          <p>
            History reflects retained records, including backdated GUIDE dates; deleted status
            events are excluded.
          </p>
        </div>
      </details>
    </section>
  );
}
